import {action, transaction} from 'mobx';
import ModelState, {ModelWrapper, Search} from './model';
import QS from 'querystring';
import autobind from 'autobind-decorator';
import setFunctionName from 'utils/setFunctionName';
import objectAssignDeep from 'utils/object-assign-deep';

// Extend the search class so we can correctly set instances without plural for CMS posts
class CmsSearch extends Search {
  @autobind @action onSuccess({response, json}) {
    this.isLoading = false;
    transaction(() => {
      const instances = json;
      for (const instance of instances) {
        this.modelState.setOneInstance(instance);
      }
      this.keys.splice(
        0,
        this.keys.length,
        ...instances.map(instance =>
          this.modelState.getModelKey(instance.id)),
      );
    });
  }
}

// Extend the model wrapper so we can correctly set instances without singular for CMS posts
class CmsModelWrapper extends ModelWrapper {
  @autobind @action onSuccess({response, json}) {
    this.isLoading = false;
    this.setInstance(json, true);
  }
}

/*
 * Extend the model state to enable us to use the cmsSdk instead for our CMS posts
 * also enable us to wrap our models / searches correctly with CMS specific functionality
 */
export default class CmsModelState extends ModelState {
  /*
   * Get a single search request with the CMS sdk
   */
  getSearchRequest(search) {
    if (!this.url) {
      throw new Error('Missing option `url`');
    }
    if (!this.plural) {
      throw new Error('Missing option `plural`');
    }
    return this.ctx.cmsSdk.get(`${this.url}?${QS.stringify(search.search)}`);
  }

  /*
   * Get a single model with the CMS sdk
   */
  getSingleRequest(model) {
    if (!this.url) {
      throw new Error('Missing option `url`');
    }
    if (!this.singular) {
      throw new Error('Missing option `singular`');
    }
    if (this.extendParam) {
      return this.ctx.cmsSdk.get(`${this.url}/${model.id}?${this.extendParam}`);
    }
    return this.ctx.cmsSdk.get(`${this.url}/${model.id}`);
  }

  /*
   * Load a search from our cache or trigger a call to get it
   */
  @autobind @action loadSearch(search) {
    const id = this.getSearchKey(search);
    if (!this.searches.has(id)) {
      this.searches.set(id, new this.CmsSearch(this, id, search));
    }
    return this.searches.get(id).load();
  }

  /*
   * Load a model from our cache or trigger a call to get it
   */
  @autobind @action loadOne(id) {
    const key = this.getModelKey(id);
    if (!this.models.has(key)) {
      this.models.set(key, new this.CmsModelWrapper(this, id));
    }

    return this.models.get(key).load();
  }

  /*
   * Set an instance of our model, check if it exists already, if it does then update it otherwise
   * just add it.
   */
  @autobind @action setOneInstance(instance) {
    let {id} = instance;
    id = this.sanitizeModelId(id);
    const key = this.getModelKey(id);
    let modelObject;
    if (!this.models.has(key)) {
      modelObject = new this.CmsModelWrapper(this, id);
      this.models.set(key, modelObject);
    } else {
      modelObject = this.models.get(key);
      modelObject.abort();
    }
    modelObject.setInstance(instance);
  }

  /*
   * Add the ability to deserialize any cms models sent from the server
   */
  @action deserialize(value) {
    const {models, searches, ...rest} = value;
    objectAssignDeep(this, rest);
    if (models) {
      for (const _key of Object.keys(models)) {
        const object = models[_key];
        const id = this.sanitizeModelId(object.id);
        const key = this.getModelKey(id);
        const model = new this.CmsModelWrapper(this, id);
        model.isLoading = object.isLoading;
        model.error = object.error;
        model.setInstance(object.instance);
        this.models.set(key, model);
      }
    }
    if (searches) {
      for (const key of Object.keys(searches)) {
        const object = searches[key];
        const search = new this.CmsSearch(this, object.key, object.search);
        search.isLoading = object.isLoading;
        search.error = object.error;
        search.keys = object.keys;
        this.searches.set(object.key, search);
      }
    }
  }

  constructor(ctx) {
    super(ctx);

    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    const className = this.singular
      .replace(/(^|[^a-z])[a-z]/mg, matches => matches[0].toUpperCase());

    if (!this.CmsSearch) {
      Object.defineProperty(this, 'CmsSearch', {
        value: class extends CmsSearch {},
        readonly: true,
        enumerable: false,
      });
      setFunctionName(this.CmsSearch, `${className}CmsSearch`);
    }

    if (!this.CmsModelWrapper) {
      Object.defineProperty(this, 'CmsModelWrapper', {
        value: class extends CmsModelWrapper {},
        readonly: true,
        enumerable: false,
      });
      setFunctionName(this.CmsModelWrapper, `${className}CmsModelWrapper`);
    }
  }
}



// WEBPACK FOOTER //
// ./spa/state/_util/cms-model.jsx